import userTeamService from '../services/userTeamService';
import { getCurrentAdviser } from './currentAdviser';
import { isAssigneeOption } from './assigneeOption';

const PARAPLANNER_DRAFT_TASKS = new Set(['soa-drafted', 'roa-eo-drafted']);

export function isParaplannerDraftTask(templateId) {
  return PARAPLANNER_DRAFT_TASKS.has(templateId);
}

/** Default assignee for Fact Find Complete — the user who created the client. */
export function resolveFactFindCompleteAssignee(clientData) {
  const email = (clientData?.createdBy || '').trim();
  if (!email) {
    const marketer = (clientData?.assignment?.assignedMarketer || '').trim();
    if (!marketer) return { email: '', name: '' };
    return {
      email: marketer,
      name: userTeamService.getFullName(marketer) || '',
    };
  }
  const name =
    clientData?.createdByName
    || userTeamService.getFullName(email)
    || '';
  return { email, name };
}

const normEmail = (email) => (email || '').trim().toLowerCase();

function mergeGlobalSuperAdminAssignees(options) {
  const global = userTeamService.getGlobalTaskAssigneeOptions() || [];
  if (!global.length) return options;
  const seen = new Set((options || []).map((opt) => normEmail(opt.value)));
  const merged = [...(options || [])];
  global.forEach((opt) => {
    const key = normEmail(opt.value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(opt);
  });
  return merged.sort((a, b) => a.label.localeCompare(b.label));
}

function appendAssigneeEmails(options, emails) {
  const seen = new Set((options || []).map((opt) => normEmail(opt.value)));
  const merged = [...(options || [])];
  (emails || []).forEach((raw) => {
    const key = normEmail(raw);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push({
      value: key,
      label: userTeamService.getFullName(key) || key,
    });
  });
  return merged.sort((a, b) => a.label.localeCompare(b.label));
}

function mergeAssigneeOptionLists(...lists) {
  const seen = new Set();
  const merged = [];
  lists.forEach((list) => {
    (list || []).forEach((opt) => {
      const key = normEmail(opt.value);
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push({ value: key, label: opt.label || key });
    });
  });
  return merged.sort((a, b) => a.label.localeCompare(b.label));
}

/** Effective assignee for gating — pending form value wins over saved task. */
export function resolveEffectiveTaskAssignee(task, assigneeEmail, clientFallbackEmail) {
  return normEmail(assigneeEmail || task?.assignedTo || clientFallbackEmail);
}

/** Whether the signed-in user may use Task Actions on this task (matches task assignee). */
export function isTaskActionsAssignee(userEmail, task, assigneeEmail) {
  const user = normEmail(userEmail);
  const assignee = resolveEffectiveTaskAssignee(task, assigneeEmail);
  return Boolean(user && assignee && user === assignee);
}

export const TASK_COMPLETE_ASSIGNEE_MESSAGE =
  'Only the assigned team member can mark this task complete.';

/** Whether the user may mark the task complete (same rule as Task Actions). */
export function canUserMarkTaskComplete(userEmail, task, assigneeEmail) {
  return isTaskActionsAssignee(userEmail, task, assigneeEmail);
}

/** The client's Assigned Adviser (SOA Approved, Welcome Call Complete, …) — src/utils/currentAdviser.js. */
export function resolveOnboardingAdviserAssignee(clientData) {
  return getCurrentAdviser(clientData);
}

/**
 * Can this staff member be handed the client's work (a task, an email)? The
 * tenant rule for opening the client (server/lib/clientAccess.cjs
 * canUserAccessClient), so one firm's staff never get another firm's client work.
 */
export function isClientStaffMember(email, clientData) {
  const key = normEmail(email);
  const config = key ? userTeamService.getUserConfig(key) : null;
  if (!config) return false;
  if (config.role === 'super_admin') return true;
  if (normEmail(clientData?.createdBy) === key) return true;
  const assignment = clientData?.assignment || {};
  if (config.role === 'advisory') return Boolean(config.advisoryTeam) && config.advisoryTeam === assignment.assignedAdvisory;
  if (config.role === 'marketing') return Boolean(config.marketingTeam) && config.marketingTeam === assignment.assignedMarketingTeam;
  if (config.role === 'paraplanner') return isParaplannerForFirm(config, assignment.assignedAdvisory);
  return false;
}

/**
 * Does this paraplanner's team serve the advice firm? A team with no authorized
 * firms serves every firm (Admin → Teams → Paraplanner Teams).
 */
export function isParaplannerForFirm(config, firmId) {
  const firms = userTeamService.getParaplannerAuthorizedAdvisoryTeams(config?.paraplannerTeam);
  return firms.length === 0 || (Boolean(firmId) && firms.includes(firmId));
}

/** Drop people whose Assignee option is unticked on Admin → Teams (src/utils/assigneeOption.js). */
export function filterToAssigneeOptions(options) {
  return (options || []).filter((opt) => isAssigneeOption(userTeamService.getUserConfig(normEmail(opt.value))));
}

/**
 * People a workflow template task can be given to: the firm's advisory staff and
 * the paraplanners whose team serves the firm, Assignee option ticked. With no
 * firm (a global template) every firm's staff and every paraplanner are offered.
 */
export function getFirmAssigneeOptions(firmId) {
  const firm = firmId && firmId !== '__global__' ? firmId : null;
  const advisory = firm ? userTeamService.getUsersByAdvisoryTeam(firm) : userTeamService.getAllAdvisoryUsers();
  const paraplanners = userTeamService.getAllParaplannerUsers().filter((opt) => (
    !firm || isParaplannerForFirm(userTeamService.getUserConfig(normEmail(opt.value)), firm)
  ));
  return {
    advisory: filterToAssigneeOptions(advisory),
    paraplanners: filterToAssigneeOptions(paraplanners),
  };
}

/**
 * Assignee dropdown options for Advice Journey / implementation task dialogs:
 * advisory users, paraplanners and global super admins — only those who can
 * open the client when `clientData` is given, and only ticked Assignee options
 * with `assigneesOnly` (the current assignee and the signed-in user always stay).
 */
export function getTaskAssigneeOptions({
  userEmail,
  currentAssigneeEmail,
  clientData = null,
  assigneesOnly = false,
}) {
  const extraEmails = [currentAssigneeEmail, userEmail].filter(Boolean);
  const base = mergeAssigneeOptionLists(
    userTeamService.getAllAdvisoryUsers(),
    userTeamService.getAllParaplannerUsers()
  );
  const all = mergeGlobalSuperAdminAssignees(base);
  const scoped = clientData ? all.filter((opt) => isClientStaffMember(opt.value, clientData)) : all;
  // Task pickers pass assigneesOnly; email recipient lists keep everyone.
  return appendAssigneeEmails(assigneesOnly ? filterToAssigneeOptions(scoped) : scoped, extraEmails);
}
